import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Modal, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

function CampaignHQ() {
  const navigate = useNavigate();
  const [userTier, setUserTier] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');

  const [funnelCount, setFunnelCount] = useState(null);
  const [promoCount, setPromoCount] = useState(null);

  useEffect(() => {
    // Fetch user's tier on mount
    const fetchUserTier = async () => {
      try {
        const response = await fetch('/api/auth/check-auth', { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          if (data.authenticated && data.user) {
            setUserTier(data.user.command_center_tier);
          }
        }
      } catch (err) {
        console.error('Failed to fetch user tier:', err);
      }
    };
    fetchUserTier();

    // Fetch counts
    const fetchCounts = async () => {
      try {
        const [funnelRes, promoRes] = await Promise.all([
          fetch('/api/funnels', { credentials: 'include' }),
          fetch('/api/planned-promotions/get-all', { credentials: 'include' }),
        ]);
        if (funnelRes.ok) {
          const data = await funnelRes.json();
          setFunnelCount(Array.isArray(data) ? data.length : (data.funnels?.length ?? 0));
        }
        if (promoRes.ok) {
          const data = await promoRes.json();
          setPromoCount(Array.isArray(data) ? data.length : (data.promotions?.length ?? 0));
        }
      } catch (err) {
        console.error('Failed to fetch counts:', err);
      }
    };
    fetchCounts();
  }, []);

  const handleTierGatedClick = (section) => {
    // Check if user has required tier access
    const tierHierarchy = { 'essential': 1, 'advanced': 2, 'ultimate': 3, 'black': 4 };
    const userLevel = tierHierarchy[userTier] || 0;
    const requiredLevel = tierHierarchy[section.requiredTier] || 0;

    if (userLevel >= requiredLevel) {
      navigate(`/command-center/campaign-hq/${section.id}`);
    } else {
      setUpgradeFeature(section.title);
      setShowUpgradeModal(true);
    }
  };

  const sectionGroups = [
    {
      label: 'Strategy & Systems',
      count: funnelCount != null && promoCount != null ? (funnelCount + promoCount) : funnelCount ?? promoCount,
      items: [
        {
          id: 'funnel-management',
          title: 'Funnel Management System',
          icon: '🔄',
          description: 'Build and manage your sales funnels.',
        },
        {
          id: 'promotions',
          title: 'Promotions',
          icon: '🎁',
          description: 'Schedule and manage promotional campaigns.',
        },
        {
          id: 'launch',
          title: '7-Phase Launch',
          icon: '🚀',
          description: 'Execute a complete product launch with our proven 7-phase strategy.',
          requiredTier: 'black',
          tierBadge: 'Black Tier',
        },
      ],
    },
    {
      label: 'On-Demand Content',
      subtitle: 'Your weekly content is generated automatically. Use these tools when you want to create something extra or on a specific topic.',
      items: [
        {
          id: 'social/long-form',
          title: 'Long Form Posts',
          icon: '📝',
          description: 'Blog posts, LinkedIn articles, Medium pieces.',
        },
        {
          id: 'social/short-form',
          title: 'Short Form',
          icon: '🐦',
          description: 'LinkedIn posts, tweets, social media content for FB/X/Threads.',
        },
        {
          id: 'social/video-scripts',
          title: 'Video Scripts',
          icon: '🎬',
          description: 'Generate video scripts from blog posts or standalone topics.',
        },
      ],
    },
    {
      label: 'Quick Campaigns',
      subtitle: <>For best results, build these into a <span onClick={() => navigate('/command-center/campaign-hq/funnel-management')} style={{ color: colors.goldDark, textDecoration: 'underline', cursor: 'pointer' }}>funnel</span>. But standalone pages work great for general site navigation — think menu items like "Work With Me" or "Free Resources."</>,
      items: [
        {
          id: 'sales-page',
          title: 'Sales Page',
          icon: '📄',
          description: 'Generate and edit your conversion-optimized sales page.',
        },
        {
          id: 'email',
          title: 'eMail Marketing',
          icon: '📧',
          description: 'Email campaigns, sequences, and newsletters.',
        },
        {
          id: 'lead-magnets',
          title: 'Lead Magnets',
          icon: '🎁',
          description: 'Free valuable content offered in exchange for contact info.',
          path: '/command-center/products/lead-magnets',
        },
      ],
    },
    {
      label: 'Coming Soon',
      items: [
        { id: 'social/pinterest', title: 'Pinterest', icon: '📌', description: 'Pin descriptions, visual content strategy.', muted: true },
        { id: 'direct-mail', title: 'Direct / Snail Mail', icon: '✉️', description: 'Physical mail campaigns and postcards.', muted: true },
        { id: 'paid-ads', title: 'Paid Ad Manager', icon: '💰', description: 'Manage paid advertising campaigns.', muted: true },
        { id: 'sms-rcs', title: 'SMS/RCS', icon: '💬', description: 'Text message and rich messaging campaigns.', muted: true },
        { id: 'voice-calls', title: 'AI Voice Calls', icon: '📞', description: 'Automated voice outreach and follow-ups.', muted: true },
        { id: 'gbp', title: 'Google Business Profile', icon: '📍', description: 'Manage your Google Business presence.', muted: true },
        { id: 'seo', title: 'SEO Suite', icon: '🔍', description: 'Search engine optimization tools.', muted: true },
        { id: 'affiliates', title: 'Affiliate Management', icon: '🤝', description: 'Manage affiliate partners and commissions.', muted: true },
        { id: 'press', title: 'Press Releases', icon: '📰', description: 'Create and distribute press releases.', muted: true },
      ],
    },
  ];

  const cardStyle = {
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: `1px solid ${colors.greyLight}`,
    height: '100%',
  };

  const cardHoverStyle = {
    borderColor: colors.goldMid,
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  };

  const iconStyle = {
    fontSize: '2rem',
    marginBottom: '0.5rem',
  };

  const titleStyle = {
    color: colors.greyDarkest,
    fontWeight: '600',
    fontSize: '1rem',
    marginBottom: '0.5rem',
  };

  return (
    <Container className="py-4">
      <div className="mb-4">
        <h1>Campaign HQ</h1>
        <p className="text-muted">Your marketing command center. Manage all your campaigns from one place.</p>
      </div>

      {sectionGroups.map((group) => (
        <div key={group.label} className="mb-4">
          <h5 style={{
            color: group.label === 'Coming Soon' ? colors.greyMid : colors.greyDarkest,
            fontWeight: 600,
            fontSize: '0.95rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: '0.75rem',
            paddingBottom: '0.5rem',
            borderBottom: `2px solid ${group.label === 'Coming Soon' ? '#e0e0e0' : colors.goldDark}`,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}>
            {group.label}
            {group.count != null && (
              <span style={{
                backgroundColor: group.count > 0 ? colors.goldDark : colors.greyLight,
                color: group.count > 0 ? '#fff' : colors.greyMid,
                fontSize: '0.75rem',
                fontWeight: 600,
                padding: '1px 8px',
                borderRadius: '10px',
                textTransform: 'none',
                letterSpacing: 'normal',
              }}>
                {group.count}
              </span>
            )}
          </h5>
          {group.subtitle && (
            <p style={{ color: colors.greyMid, fontSize: '0.85rem', marginTop: '-0.25rem', marginBottom: '0.75rem' }}>
              {group.subtitle}
            </p>
          )}
          <Row>
            {group.items.map((section) => {
              // Coming Soon items keep the old compact style
              if (section.muted) {
                return (
                  <Col key={section.id} lg={3} md={4} sm={6} className="mb-3">
                    <Card
                      style={{ ...cardStyle, opacity: 0.4, cursor: 'default' }}
                      className="shadow-sm"
                      title="Coming soon"
                    >
                      <Card.Body className="text-center p-3">
                        <div style={iconStyle}>{section.icon}</div>
                        <div style={titleStyle}>{section.title}</div>
                        <Card.Text className="text-muted small mb-0">
                          {section.description}
                        </Card.Text>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              }

              // Active items use the standard hub card pattern
              return (
                <Col key={section.id} md={6} className="mb-3">
                  <Card
                    className="h-100 shadow-sm"
                    style={{
                      border: section.requiredTier ? '2px solid #1a1a1a' : `1px solid ${colors.greyLight}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onClick={() => {
                      if (section.requiredTier) {
                        handleTierGatedClick(section);
                      } else {
                        navigate(section.path || `/command-center/campaign-hq/${section.id}`);
                      }
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = section.requiredTier ? '#333' : colors.goldMid;
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = section.requiredTier ? '#1a1a1a' : colors.greyLight;
                      e.currentTarget.style.boxShadow = '0 .125rem .25rem rgba(0,0,0,.075)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <Card.Header style={{
                      backgroundColor: colors.greyDarkest,
                      color: colors.goldLight,
                      fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif",
                      letterSpacing: '0.1em',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                      <strong>{section.title}</strong>
                      {section.tierBadge && (
                        <span style={{
                          backgroundColor: '#fff',
                          color: '#1a1a1a',
                          fontSize: '0.65rem',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontWeight: '600',
                        }}>
                          {section.tierBadge}
                        </span>
                      )}
                    </Card.Header>
                    <Card.Body className="d-flex flex-column p-4">
                      <p className="text-muted flex-grow-1">{section.description}</p>
                      <Button
                        variant="outline-dark"
                        className="mt-auto"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (section.requiredTier) {
                            handleTierGatedClick(section);
                          } else {
                            navigate(section.path || `/command-center/campaign-hq/${section.id}`);
                          }
                        }}
                      >
                        Open {section.title} →
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>
      ))}

      {/* Upgrade Modal for tier-gated features */}
      <Modal show={showUpgradeModal} onHide={() => setShowUpgradeModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: '#1a1a1a', color: '#fff', border: 'none' }}>
          <Modal.Title>Black Tier Feature</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🖤</div>
          <h4>{upgradeFeature}</h4>
          <p className="text-muted">
            This feature is exclusively available to Black Tier members.
          </p>
          <p>
            The Black Tier unlocks our most powerful tools including the 7-Phase Launch Strategy,
            10 parallel funnels, and unlimited content capacity.
          </p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center border-0">
          <Button
            variant="dark"
            href="mailto:strategy@aiprofitengineer.com?subject=Black%20Tier%20Inquiry"
          >
            Inquire About Black Tier
          </Button>
          <Button variant="outline-secondary" onClick={() => setShowUpgradeModal(false)}>
            Maybe Later
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default CampaignHQ;
